import { useCallback, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tag, Modal, message, Select, Alert } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getNotificationPreferences,
  deleteNotificationPreference,
  type NotificationPreference,
  type NotificationPreferenceFilters,
} from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';
import AdminNav from './AdminNav';

const CHANNELS = ['email', 'in_app', 'sms', 'push'];
const EVENT_TYPES = ['ticket_assigned', 'deal_won', 'deal_lost', 'invoice_generated', 'subscription_expiring', 'workflow_completed'];

export default function NotificationPrefListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [channelFilter, setChannelFilter] = useState<string | undefined>();
  const [eventTypeFilter, setEventTypeFilter] = useState<string | undefined>();

  const canRead = hasPermission('admin', 'read');
  const canWrite = hasPermission('admin', 'write');
  const canDelete = hasPermission('admin', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: NotificationPreferenceFilters = { ...params };
      if (channelFilter) filters.channel = channelFilter;
      if (eventTypeFilter) filters.event_type = eventTypeFilter;
      return getNotificationPreferences(filters);
    },
    [channelFilter, eventTypeFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('admin.confirm_delete_notification_pref'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteNotificationPreference(id);
            message.success(t('admin.notification_pref_delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<NotificationPreference> = useMemo(
    () => [
      {
        title: t('admin.notification_field.user_id'),
        dataIndex: 'user_id',
        key: 'user_id',
        ellipsis: true,
      },
      {
        title: t('admin.notification_field.channel'),
        dataIndex: 'channel',
        key: 'channel',
        width: 100,
        render: (ch: string) => (
          <Tag color="blue">{t(`admin.channel_options.${ch}`, ch)}</Tag>
        ),
      },
      {
        title: t('admin.notification_field.event_type'),
        dataIndex: 'event_type',
        key: 'event_type',
        width: 180,
        render: (et: string) => t(`admin.event_type_options.${et}`, et),
      },
      {
        title: t('admin.notification_field.is_enabled'),
        dataIndex: 'is_enabled',
        key: 'is_enabled',
        width: 90,
        render: (v: boolean) => (
          <Tag color={v ? 'green' : 'red'}>
            {v ? t('app.yes') : t('app.no')}
          </Tag>
        ),
      },
      {
        title: t('admin.notification_field.frequency'),
        dataIndex: 'frequency',
        key: 'frequency',
        width: 130,
        render: (f: string) => t(`admin.frequency_options.${f}`, f),
      },
      {
        title: t('admin.notification_field.quiet_hours_start'),
        dataIndex: 'quiet_hours_start',
        key: 'quiet_hours_start',
        width: 120,
        render: (v: string | null) => v || '-',
      },
      {
        title: t('admin.notification_field.quiet_hours_end'),
        dataIndex: 'quiet_hours_end',
        key: 'quiet_hours_end',
        width: 120,
        render: (v: string | null) => v || '-',
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 100,
        render: (_: unknown, record: NotificationPreference) => (
          <Space size="small">
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/admin/notification-preferences/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete]
  );

  const toolbar = (
    <Space>
      <Select
        allowClear
        placeholder={t('admin.notification_field.channel')}
        value={channelFilter}
        onChange={(v) => { setChannelFilter(v); gridRef.current?.reload(); }}
        style={{ width: 130 }}
        options={CHANNELS.map((c) => ({ value: c, label: t(`admin.channel_options.${c}`) }))}
      />
      <Select
        allowClear
        placeholder={t('admin.notification_field.event_type')}
        value={eventTypeFilter}
        onChange={(v) => { setEventTypeFilter(v); gridRef.current?.reload(); }}
        style={{ width: 180 }}
        options={EVENT_TYPES.map((e) => ({ value: e, label: t(`admin.event_type_options.${e}`) }))}
      />
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/admin/notification-preferences/new')}
        >
          {t('admin.create_notification_pref_title')}
        </Button>
      )}
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <AdminNav />
      <h2>{t('admin.notification_prefs_title')}</h2>
      <DataGrid<NotificationPreference>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('admin.search_notification_prefs_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
